// <s-scroll-carousel> adds previous/next buttons and position dots to a native
// scroll-snap track. Scrolling stays native (touch, trackpad, keyboard); this only
// wires the buttons and highlights the dot for the first visible slide.
// Markup contract: [data-track] scroller with <li> slides, [data-prev], [data-next],
// and [data-dots] container holding one [data-dot] button per slide.
class ScrollCarousel extends HTMLElement {
  #track = null;
  #slides = [];
  #dots = [];
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector("[data-track]");
    if (!this.#track) return;
    this.#slides = [...this.#track.children];
    this.#dots = [...this.querySelectorAll("[data-dot]")];

    this.querySelector("[data-prev]")?.addEventListener("click", () => this.#step(-1));
    this.querySelector("[data-next]")?.addEventListener("click", () => this.#step(1));
    this.#dots.forEach((dot, index) => dot.addEventListener("click", () => this.#goTo(index)));
    this.#track.addEventListener("scroll", this.#onScroll, { passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener("scroll", this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #currentIndex() {
    const start = this.#track.getBoundingClientRect().left;
    let best = 0;
    let bestDistance = Infinity;
    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - start);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #step(direction) {
    const rtl = getComputedStyle(this).direction === "rtl";
    this.#goTo(this.#currentIndex() + (rtl ? -direction : direction));
  }

  #goTo(index) {
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    const smooth = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.#slides[clamped]?.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest", inline: "start" });
  }

  #sync() {
    const current = this.#currentIndex();
    this.#dots.forEach((dot, index) => {
      const active = index === current;
      dot.toggleAttribute("data-active", active);
      if (active) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
    });
  }
}

if (!customElements.get("s-scroll-carousel")) customElements.define("s-scroll-carousel", ScrollCarousel);
